<template>
  <div v-if="article" class="article-page">
    <div class="article-container">
      <div class="article-header">
        <span class="article-category">{{ article.category }}</span>
        <h1 class="article-title">{{ article.title }}</h1>
        <p v-if="article.subtitle" class="article-subtitle">{{ article.subtitle }}</p>
        <div class="article-meta">
          <span class="article-date">{{ article.date }}</span>
        </div>
      </div>

      <div class="article-image-container">
        <img :src="article.image" :alt="article.title" class="article-image" />
      </div>

      <div class="article-content" v-html="article.content"></div>

      <div class="article-actions">
        <button @click="goBack" class="btn-back">
          <Icon icon="material-symbols:arrow-back-rounded" />
          Voltar
        </button>
      </div>
    </div>

    <div class="related-news" v-if="relatedNews.length">
      <h2 class="related-title">Outras notícias</h2>
      <div class="related-grid">
        <router-link 
          v-for="news in relatedNews" 
          :key="news.id"
          :to="`/noticia/${news.id}`"
          class="related-card"
        >
          <div class="related-image" :style="{ backgroundImage: `url(${news.image})` }"></div>
          <span class="related-category">{{ news.category }}</span>
          <h3 class="related-card-title">{{ news.title }}</h3>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { getNewsById, getRelatedNews } from '../data/newsData'

const buildFallbackArticle = (id) => ({
  id,
  category: 'NOTÍCIAS',
  title: 'Notícia não encontrada',
  subtitle: '',
  date: new Date().toLocaleDateString('pt-BR'),
  image: 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=1200&h=600&fit=crop',
  content: '<p>Esta notícia não está disponível no momento.</p>'
});

export default {
  name: 'ArticleView',
  components: {
    Icon
  },
  data() {
    return {
      article: null,
      relatedNews: []
    }
  },
  created() {
    this.fetchArticle()
  },
  watch: {
    '$route.params.id'() {
      this.fetchArticle()
    }
  },
  methods: {
    async fetchArticle() {
      const articleId = Number(this.$route.params.id)
      this.article = null
      this.relatedNews = []

      try {
        const article = await getNewsById(articleId)
        this.article = article || buildFallbackArticle(articleId)

        const related = await getRelatedNews(articleId, 3)
        this.relatedNews = (related || [])
          .filter(item => Number(item.id) !== articleId)
          .slice(0, 3)
      } catch (error) {
        console.error('Erro ao carregar notícia', error)
        this.article = buildFallbackArticle(articleId)
        this.relatedNews = []
      }
    },
    goBack() {
      this.$router.go(-1);
    }
  }
}
</script>

<style scoped>
.article-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.article-container {
  max-width: 900px;
  margin: 0 auto 4rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid rgba(0, 35, 73, 0.12);
  box-shadow: 0 30px 80px rgba(0, 35, 73, 0.1);
}

.article-header {
  margin-bottom: 2rem;
}

.article-category {
  display: inline-block;
  background: var(--brand-navy);
  color: #f5f8ff;
  padding: 0.4rem 1.2rem;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1.5rem;
}

.article-title {
  font-size: 32px;
  font-weight: 800;
  line-height: 1.2;
  color: var(--brand-navy);
  margin-bottom: 1rem;
  letter-spacing: -0.5px;
}

.article-subtitle {
  font-size: 21px;
  line-height: 1.6;
  color: #475569;
  margin-bottom: 1.5rem;
  font-weight: 400;
}

.article-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: 14px;
  color: rgba(0, 35, 73, 0.65);
  padding-top: 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
}

.article-author {
  font-weight: 600;
  color: #3b82f6;
}

.article-image-container {
  margin: 2rem 0;
  border-radius: 12px;
  overflow: hidden;
}

.article-image {
  width: 100%;
  height: auto;
  display: block;
}

.article-content {
  font-size: 18px;
  line-height: 1.8;
  color: rgba(0, 35, 73, 0.95);
  margin: 2rem 0;
  text-align: justify;
}

.article-content :deep(p) {
  margin-bottom: 1.5rem;
}

.article-content :deep(p:last-child) {
  margin-bottom: 0;
}

.article-actions {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--brand-navy);
  color: #f5f8ff;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 18px 35px rgba(0, 35, 73, 0.2);
}

.btn-back:hover {
  transform: translateX(-5px);
  box-shadow: 0 10px 24px rgba(0, 35, 73, 0.35);
}

/* Notícias relacionadas */
.related-news {
  margin-top: 4rem;
}

.related-title {
  font-size: 32px;
  font-weight: 800;
  color: var(--brand-navy);
  margin-bottom: 2rem;
  text-align: center;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.related-card {
  background: rgba(255, 255, 255, 0.95);
  color: rgba(0, 35, 73, 0.95);
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid rgba(0, 35, 73, 0.1);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  display: block;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.related-card:hover {
  color: rgba(0, 35, 73, 0.95);
  transform: translateY(-8px);
  box-shadow: 0 30px 60px rgba(0, 35, 73, 0.18);
  border-color: rgba(0, 35, 73, 0.25);
}

.related-image {
  height: 180px;
  background-size: cover;
  background-position: center;
}

.related-category {
  display: inline-block;
  background: rgba(0, 35, 73, 0.08);
  color: var(--brand-navy);
  padding: 0.35rem 0.95rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 1rem 1rem 0.5rem;
  box-shadow: 0 10px 20px rgba(0, 35, 73, 0.12);
}

.related-card-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2a37;
  padding: 0 1rem 1.4rem;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .article-container {
    padding: 2rem 1.5rem;
  }
  
  .article-title {
    font-size: 24px;
  }
  
  .article-subtitle {
    font-size: 18px;
  }
  
  .article-content {
    font-size: 16px;
  }
  
  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
